import { FC } from "react";
import { StepsProps } from "../../types";
import { Button } from "@/components/ui";
import { FormProvider } from "react-hook-form";
import useStep2 from "./useStep2";
import {
  FormCheckbox,
  FormDatePicker,
  FormInput,
  FormPhoneInput,
  FormSectionHeader,
  FormSelect,
  Loader,
} from "@/components";
import {
  ADDRESS,
  ADDRESS_1,
  ADDRESS_2,
  ALTERNATE_LOGIN_ID,
  BOM_END_DATE,
  BOM_POSITION,
  BOM_START_DATE,
  CITY,
  EMAIL_ID_1,
  EMAIL_ID_2,
  END_DATE,
  FIRST_NAME,
  IS_BOM_USER,
  LANDLINE_NO,
  LAST_NAME,
  MOBILE_NO,
  OCCUPANCY_TYPE,
  occupancyTypes,
  PO_BOX,
  START_DATE,
  STATE,
  USERNAME,
  ZIPCODE,
} from "./consts";
import moment from "moment";

const Step2: FC<StepsProps> = ({ onCancel, isView }) => {
  const {
    form,
    requiredFields,
    stateList,
    bomPositions,
    isBOMPositionsLoading,
    isUserDetailsLoading,
    isPropertyOwnerDetailsLoading,
    stateKey,
    isUserDetailsRefetching,
    isPropertyOwnerDetailsRefetching,
    isBomUserDetailsRefetching,
  } = useStep2();
  const { errors } = form.formState;

  return isBOMPositionsLoading ||
    isUserDetailsLoading ||
    isPropertyOwnerDetailsLoading ||
    isUserDetailsRefetching ||
    isPropertyOwnerDetailsRefetching ||
    isBomUserDetailsRefetching ? (
    <Loader />
  ) : (
    <FormProvider {...form}>
      <form>
        <div className="bg-white mt-[20px] rounded-lg px-[30px] py-[25px]">
          <div>
            <FormSectionHeader>User Info</FormSectionHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px]">
              <FormInput
                name={USERNAME}
                control={form.control}
                label="User ID"
                placeholder="Please enter"
                error={errors[USERNAME]?.message}
                isRequired={requiredFields.includes(USERNAME)}
                disabled={isView}
                noSpace
              />
              <FormInput
                name={ALTERNATE_LOGIN_ID}
                control={form.control}
                label="Alternative Login ID"
                placeholder="Please enter"
                error={errors[ALTERNATE_LOGIN_ID]?.message}
                isRequired={requiredFields.includes(ALTERNATE_LOGIN_ID)}
                disabled={isView}
                noSpace
              />
              <FormInput
                name={FIRST_NAME}
                control={form.control}
                label="First Name"
                placeholder="Please enter"
                error={errors[FIRST_NAME]?.message}
                isRequired={requiredFields.includes(FIRST_NAME)}
                disabled={isView}
              />
              <FormInput
                name={LAST_NAME}
                control={form.control}
                label="Last Name"
                placeholder="Please enter"
                error={errors[LAST_NAME]?.message}
                isRequired={requiredFields.includes(LAST_NAME)}
                disabled={isView}
              />
              <FormPhoneInput
                name={MOBILE_NO}
                control={form.control}
                label="Mobile No"
                placeholder="Please enter"
                error={errors[MOBILE_NO]?.message}
                isRequired={requiredFields.includes(MOBILE_NO)}
                disabled={isView}
              />
              <FormPhoneInput
                name={LANDLINE_NO}
                control={form.control}
                label="Landline No"
                placeholder="Please enter"
                error={errors[LANDLINE_NO]?.message}
                isRequired={requiredFields.includes(LANDLINE_NO)}
                disabled={isView}
              />
              <FormInput
                name={EMAIL_ID_1}
                control={form.control}
                label="Email ID 1"
                placeholder="Please enter"
                error={errors[EMAIL_ID_1]?.message}
                isRequired={requiredFields.includes(EMAIL_ID_1)}
                disabled={isView}
              />
              <FormInput
                name={EMAIL_ID_2}
                control={form.control}
                label="Email ID 2"
                placeholder="Please enter"
                error={errors[EMAIL_ID_2]?.message}
                isRequired={requiredFields.includes(EMAIL_ID_2)}
                disabled={isView}
              />
            </div>
          </div>
          <div className="mt-8">
            <FormSectionHeader>Address</FormSectionHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px]">
              <FormInput
                name={`${ADDRESS}.${ADDRESS_1}`}
                control={form.control}
                label="Address 1"
                placeholder="Please enter"
                error={errors[`${ADDRESS}.${ADDRESS_1}`]?.message}
                isRequired={requiredFields.includes(`${ADDRESS}.${ADDRESS_1}`)}
                disabled={isView}
              />
              <FormInput
                name={`${ADDRESS}.${ADDRESS_2}`}
                control={form.control}
                label="Address 2"
                placeholder="Please enter"
                error={errors[`${ADDRESS}.${ADDRESS_2}`]?.message}
                isRequired={requiredFields.includes(`${ADDRESS}.${ADDRESS_2}`)}
                disabled={isView}
              />
              <FormSelect
                key={stateKey}
                name={`${ADDRESS}.${STATE}`}
                control={form.control}
                label="State"
                placeholder="Please select"
                error={errors[`${ADDRESS}.${STATE}`]?.message}
                isRequired={requiredFields.includes(`${ADDRESS}.${STATE}`)}
                options={stateList.map((data: any) => ({
                  label: data.name,
                  value: data.code,
                }))}
                disabled={isView}
              />
              <FormInput
                name={`${ADDRESS}.${CITY}`}
                control={form.control}
                label="City"
                placeholder="Please select"
                error={errors[`${ADDRESS}.${CITY}`]?.message}
                isRequired={requiredFields.includes(`${ADDRESS}.${CITY}`)}
                disabled={isView}
                variant="char"
              />
              <FormInput
                name={`${ADDRESS}.${ZIPCODE}`}
                control={form.control}
                label="Zipcode"
                placeholder="Please enter"
                error={errors[`${ADDRESS}.${ZIPCODE}`]?.message}
                isRequired={requiredFields.includes(`${ADDRESS}.${ZIPCODE}`)}
                maxLength={10}
                disabled={isView}
                variant="integerSpeChar"
              />
              <FormInput
                name={`${ADDRESS}.${PO_BOX}`}
                control={form.control}
                label="PO Box"
                placeholder="Please enter"
                error={errors[`${ADDRESS}.${PO_BOX}`]?.message}
                isRequired={requiredFields.includes(`${ADDRESS}.${PO_BOX}`)}
                minLength={3}
                maxLength={3}
                variant="integer"
                disabled={isView}
              />
            </div>
          </div>
          <div className="mt-8">
            <FormSectionHeader>Occupancy Type</FormSectionHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px]">
              <FormSelect
                name={OCCUPANCY_TYPE}
                control={form.control}
                label="Type"
                placeholder="Please Select"
                error={errors[OCCUPANCY_TYPE]?.message}
                isRequired={requiredFields.includes(OCCUPANCY_TYPE)}
                options={occupancyTypes}
                disabled={isView || form.watch(END_DATE)}
              />
              <FormDatePicker
                name={START_DATE}
                control={form.control}
                label="Start Date"
                placeholder="Please select"
                error={errors[START_DATE]?.message}
                isRequired={true}
                disabled={isView || form.watch(END_DATE)}
                maxDate={new Date()}
                onClear={() => {
                  form.setValue(END_DATE, "");
                  form.setValue(BOM_START_DATE, "");
                }}
                onChange={(date: any) => {
                  if (moment(date).isAfter(form.getValues()[END_DATE])) {
                    form.setValue(END_DATE, "");
                  }
                  if (moment(date).isAfter(form.getValues()[BOM_START_DATE])) {
                    form.setValue(BOM_START_DATE, "");
                  }
                }}
              />
              <FormDatePicker
                name={END_DATE}
                control={form.control}
                label="End Date"
                placeholder="Please select"
                error={errors[END_DATE]?.message}
                isRequired={requiredFields.includes(END_DATE)}
                disabled={
                  isView || form.watch(END_DATE) || !form.watch(START_DATE)
                }
                minDate={new Date(form.watch(START_DATE))}
              />
            </div>
          </div>
          <div className="mt-8">
            <FormSectionHeader>BM</FormSectionHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-[30px]">
              <FormCheckbox
                name={IS_BOM_USER}
                control={form.control}
                label="Is BM?"
                error={errors[IS_BOM_USER]?.message}
                isRequired={requiredFields.includes(IS_BOM_USER)}
                disabled={
                  isView || form.watch(END_DATE) || !bomPositions?.data?.length
                }
              />
              {form.watch(IS_BOM_USER) && (
                <>
                  <FormSelect
                    name={BOM_POSITION}
                    control={form.control}
                    label="Position"
                    placeholder="Please Select"
                    error={errors[BOM_POSITION]?.message}
                    isRequired={true}
                    options={
                      bomPositions?.data?.map((position: any) => ({
                        label: position.positionName,
                        value: position.bomPositionId?.toString(),
                      })) || []
                    }
                    disabled={
                      isView ||
                      form.watch(END_DATE) ||
                      !bomPositions?.data?.length
                    }
                  />
                  <FormDatePicker
                    name={BOM_START_DATE}
                    control={form.control}
                    label="Start Date"
                    placeholder="Please select"
                    error={errors[BOM_START_DATE]?.message}
                    isRequired={true}
                    disabled={
                      isView ||
                      form.watch(END_DATE) ||
                      !form.watch(START_DATE) ||
                      !bomPositions?.data?.length
                    }
                    minDate={new Date(form.watch(START_DATE))}
                    maxDate={new Date()}
                    info={
                      <span>
                        Please first select <b>Occupancy Start Date</b>
                      </span>
                    }
                  />
                  <FormDatePicker
                    name={BOM_END_DATE}
                    control={form.control}
                    label="End Date"
                    placeholder="Please select"
                    error={errors[BOM_END_DATE]?.message}
                    isRequired={requiredFields.includes(BOM_END_DATE)}
                    disabled={
                      isView ||
                      form.watch(END_DATE) ||
                      !form.watch(BOM_START_DATE) ||
                      !bomPositions?.data?.length
                    }
                    minDate={new Date(form.watch(BOM_START_DATE))}
                  />
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-row justify-between items-center  py-3">
          <div className="flex flex-row justify-end gap-3  py-3">
            <Button variant="outline" type="button" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default Step2;
